import { siteInfo } from '@/mocks/site';
import { aboutData } from '@/mocks/about';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: `${siteInfo.name}品牌介绍、发展历程与联系方式`,
    url: 'https://91wang.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: 'https://91wang.com/',
    },
  };

  return (
    <>
      <SEOHead
        title={`关于我们 - ${siteInfo.name} | 品牌介绍与联系方式`}
        description={`了解${siteInfo.name}的品牌故事、发展历程和核心价值。${siteInfo.description}联系我们获取更多信息。`}
        keywords="91网,关于91网,91网品牌,91网联系方式,91网介绍"
        canonicalUrl="https://91wang.com/about"
        schema={aboutSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mt-4">
              {aboutData.brandIntro.title}
            </h2>
            <p className="mt-2 text-foreground-500 text-sm md:text-base">
              {aboutData.brandIntro.subtitle}
            </p>
          </div>
        </div>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h3 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6">品牌介绍</h3>
            <div className="space-y-4">
              {aboutData.brandIntro.paragraphs.map((p, i) => (
                <p key={i} className="text-foreground-700 leading-relaxed text-sm md:text-base">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h3 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6">品牌声誉</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
              {aboutData.values.map((v, i) => (
                <div
                  key={i}
                  className="p-5 bg-background-50 rounded-lg border border-background-200/70 text-center cursor-default"
                >
                  <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-3">
                    <i className={`${v.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h4 className="text-base font-semibold text-foreground-900 mb-1">{v.title}</h4>
                  <p className="text-xs text-foreground-500 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>

            <h3 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6">发展历程</h3>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-8">
                {aboutData.milestones.map((m, i) => (
                  <div
                    key={i}
                    className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                      i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="md:w-1/2 flex items-center gap-4 md:justify-end">
                      {i % 2 === 0 ? (
                        <>
                          <div className="hidden md:block flex-1"></div>
                          <div className="bg-background-50 rounded-lg border border-background-200/70 p-4 md:text-right flex-1">
                            <span className="text-xs font-bold text-primary-500">{m.year}</span>
                            <h4 className="text-sm font-semibold text-foreground-900 mt-1">{m.title}</h4>
                            <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{m.description}</p>
                          </div>
                        </>
                      ) : (
                        <div className="pl-10 md:pl-0 md:hidden bg-background-50 rounded-lg border border-background-200/70 p-4">
                          <span className="text-xs font-bold text-primary-500">{m.year}</span>
                          <h4 className="text-sm font-semibold text-foreground-900 mt-1">{m.title}</h4>
                          <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{m.description}</p>
                        </div>
                      )}
                    </div>
                    <div className="absolute left-4 md:left-1/2 top-1 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 -translate-x-1/2 z-10"></div>
                    <div className="md:w-1/2 pl-10 md:pl-0">
                      {i % 2 === 0 ? (
                        <div className="md:hidden bg-background-50 rounded-lg border border-background-200/70 p-4">
                          <span className="text-xs font-bold text-primary-500">{m.year}</span>
                          <h4 className="text-sm font-semibold text-foreground-900 mt-1">{m.title}</h4>
                          <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{m.description}</p>
                        </div>
                      ) : (
                        <div className="hidden md:block bg-background-50 rounded-lg border border-background-200/70 p-4">
                          <span className="text-xs font-bold text-primary-500">{m.year}</span>
                          <h4 className="text-sm font-semibold text-foreground-900 mt-1">{m.title}</h4>
                          <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{m.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50" id="contact">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h3 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 text-center">联系我们</h3>
            <p className="text-center text-foreground-500 text-sm mb-10 max-w-lg mx-auto">
              如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="p-5 bg-background-100 rounded-lg border border-background-200/70 text-center cursor-default">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-line text-xl text-primary-500"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                <p className="text-xs text-foreground-500">{aboutData.contactInfo.email}</p>
              </div>
              <div className="p-5 bg-background-100 rounded-lg border border-background-200/70 text-center cursor-default">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-wechat-line text-xl text-primary-500"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                <p className="text-xs text-foreground-500">{aboutData.contactInfo.wechat}</p>
              </div>
              <div className="p-5 bg-background-100 rounded-lg border border-background-200/70 text-center cursor-default">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-qq-line text-xl text-primary-500"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">QQ 群</h4>
                <p className="text-xs text-foreground-500">{aboutData.contactInfo.qq}</p>
              </div>
              <div className="p-5 bg-background-100 rounded-lg border border-background-200/70 text-center cursor-default">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-xl text-primary-500"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h4>
                <p className="text-xs text-foreground-500">{aboutData.contactInfo.workHours}</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}